import { APARAT_TO_YOUTUBE } from '../config/videoAlternates';

export interface YoutubeDetails {
  videoId: string;
  embedUrl: string;
  watchUrl: string;
  thumbnailUrl: string;
  startTime?: number;
}

/**
 * Parses time expressions like '1m30s', '90s', '90' into seconds
 */
export function parseTimeInSeconds(tStr: string | null | undefined): number | undefined {
  if (!tStr) return undefined;
  const trimmed = tStr.trim();
  if (!trimmed) return undefined;

  // Pure number
  if (/^\d+$/.test(trimmed)) {
    return parseInt(trimmed, 10);
  }

  // Combined e.g. 1h20m30s or 2m15s
  let totalSeconds = 0;
  const hoursMatch = trimmed.match(/(\d+)h/i);
  const minsMatch = trimmed.match(/(\d+)m/i);
  const secsMatch = trimmed.match(/(\d+)s/i);

  if (hoursMatch || minsMatch || secsMatch) {
    if (hoursMatch) totalSeconds += parseInt(hoursMatch[1], 10) * 3600;
    if (minsMatch) totalSeconds += parseInt(minsMatch[1], 10) * 60;
    if (secsMatch) totalSeconds += parseInt(secsMatch[1], 10);
    return totalSeconds > 0 ? totalSeconds : undefined;
  }

  const num = parseInt(trimmed, 10);
  return isNaN(num) ? undefined : num;
}

/**
 * Comprehensive parser for any YouTube URL variant:
 * - youtube.com/watch?v=VIDEO_ID
 * - youtu.be/VIDEO_ID
 * - youtube.com/embed/VIDEO_ID
 * - youtube.com/shorts/VIDEO_ID
 * - youtube.com/live/VIDEO_ID
 * - m.youtube.com/...
 * - URLs with extra parameters (?si=..., &feature=..., ?t=...)
 * - URLs lacking protocol (youtube.com/...)
 */
export function parseYoutubeUrl(rawUrl: string): YoutubeDetails | null {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  const url = rawUrl.trim();
  if (!url) return null;

  // Ensure protocol for safe URL parsing
  const normalizedUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;

  let videoId: string | null = null;
  let startTime: number | undefined = undefined;

  try {
    const parsed = new URL(normalizedUrl);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '').replace(/^m\./, '');

    // Check time param (t or start)
    const tParam = parsed.searchParams.get('t') || parsed.searchParams.get('start');
    startTime = parseTimeInSeconds(tParam);

    if (host === 'youtu.be') {
      // youtu.be/<id>
      const pathname = parsed.pathname.replace(/^\/+/, '');
      const idCandidate = pathname.split('/')[0];
      if (idCandidate && /^[a-zA-Z0-9_-]{10,12}$/.test(idCandidate)) {
        videoId = idCandidate;
      }
    } else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
      const pathname = parsed.pathname;

      if (pathname.startsWith('/watch')) {
        // ?v=<id>
        const v = parsed.searchParams.get('v');
        if (v && /^[a-zA-Z0-9_-]{10,12}$/.test(v)) {
          videoId = v;
        }
      } else if (pathname.startsWith('/embed/')) {
        const idCandidate = pathname.split('/embed/')[1]?.split('/')[0]?.split('?')[0];
        if (idCandidate && /^[a-zA-Z0-9_-]{10,12}$/.test(idCandidate)) {
          videoId = idCandidate;
        }
      } else if (pathname.startsWith('/shorts/')) {
        const idCandidate = pathname.split('/shorts/')[1]?.split('/')[0]?.split('?')[0];
        if (idCandidate && /^[a-zA-Z0-9_-]{10,12}$/.test(idCandidate)) {
          videoId = idCandidate;
        }
      } else if (pathname.startsWith('/live/')) {
        const idCandidate = pathname.split('/live/')[1]?.split('/')[0]?.split('?')[0];
        if (idCandidate && /^[a-zA-Z0-9_-]{10,12}$/.test(idCandidate)) {
          videoId = idCandidate;
        }
      } else if (pathname.startsWith('/v/')) {
        const idCandidate = pathname.split('/v/')[1]?.split('/')[0]?.split('?')[0];
        if (idCandidate && /^[a-zA-Z0-9_-]{10,12}$/.test(idCandidate)) {
          videoId = idCandidate;
        }
      }
    }
  } catch {
    // URL parsing fallback via regex
  }

  // Fallback regex matching if URL parsing did not yield an ID
  if (!videoId) {
    const patterns = [
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?|shorts|live)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i,
      /^[a-zA-Z0-9_-]{11}$/ // Direct 11-char ID
    ];

    for (const pat of patterns) {
      const match = url.match(pat);
      if (match && match[1]) {
        videoId = match[1];
        break;
      } else if (match && !match[1] && pat.source.startsWith('^[a-zA-Z0-9_-]{11}$')) {
        videoId = url;
        break;
      }
    }
  }

  if (!videoId) return null;

  // Construct secure, privacy-enhanced embed URL
  const queryParams = new URLSearchParams();
  queryParams.set('rel', '0');
  queryParams.set('enablejsapi', '1');
  queryParams.set('origin', typeof window !== 'undefined' ? window.location.origin : 'https://activelegend.ir');
  if (startTime) {
    queryParams.set('start', startTime.toString());
  }

  const embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}?${queryParams.toString()}`;
  const watchUrl = `https://www.youtube.com/watch?v=${videoId}${startTime ? `&t=${startTime}s` : ''}`;
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  return {
    videoId,
    embedUrl,
    watchUrl,
    thumbnailUrl,
    startTime,
  };
}

export function extractYoutubeId(url: string): string | null {
  const details = parseYoutubeUrl(url);
  return details ? details.videoId : null;
}

export function extractAparatHash(url: string): string | null {
  try {
    if (!url || typeof url !== 'string' || !url.includes('aparat.com')) return null;
    const clean = url.trim().split('?')[0].split('#')[0];
    const parts = clean.split('/').filter(Boolean);
    const lastPart = parts[parts.length - 1];
    return lastPart && lastPart.length > 2 ? lastPart : null;
  } catch {
    return null;
  }
}

export function extractVimeoId(url: string): string | null {
  try {
    if (!url || typeof url !== 'string' || !url.includes('vimeo.com')) return null;
    const match = url.match(/vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|)(\d+)/);
    if (match && match[1]) return match[1];
    const clean = url.trim().split('?')[0];
    const lastPart = clean.split('/').filter(Boolean).pop();
    return lastPart && /^\d+$/.test(lastPart) ? lastPart : null;
  } catch {
    return null;
  }
}

export function isDirectVideoUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  const clean = url.trim().toLowerCase().split('?')[0];
  return clean.endsWith('.mp4') || clean.endsWith('.webm') || clean.endsWith('.ogg');
}

export type VideoEmbedResult =
  | { kind: 'youtube'; src: string; watchUrl: string; videoId: string; thumbnailUrl: string }
  | { kind: 'aparat'; src: string; rawUrl: string }
  | { kind: 'vimeo'; src: string }
  | { kind: 'direct'; src: string }
  | { kind: 'iframe'; src: string }
  | { kind: 'unavailable'; reason?: string; rawUrl: string };

/**
 * Resolves any video URL into a valid embed structure with region intelligence
 */
export function resolveVideoEmbed(rawUrl: string, isIran = false): VideoEmbedResult {
  if (!rawUrl || typeof rawUrl !== 'string') {
    return { kind: 'unavailable', rawUrl: '' };
  }
  const url = rawUrl.trim();
  if (!url) {
    return { kind: 'unavailable', rawUrl: '' };
  }

  // 1. YouTube
  const yt = parseYoutubeUrl(url);
  if (yt) {
    return {
      kind: 'youtube',
      src: yt.embedUrl,
      watchUrl: yt.watchUrl,
      videoId: yt.videoId,
      thumbnailUrl: yt.thumbnailUrl,
    };
  }

  // 2. Aparat
  if (url.includes('aparat.com')) {
    const hash = extractAparatHash(url);
    if (hash) {
      if (isIran) {
        return {
          kind: 'aparat',
          src: `https://www.aparat.com/video/video/embed/videohash/${hash}/vt/frame`,
          rawUrl: url,
        };
      }
      // If outside Iran, check if an official YouTube mirror exists in dictionary
      if (APARAT_TO_YOUTUBE[hash]) {
        const ytId = APARAT_TO_YOUTUBE[hash];
        return {
          kind: 'youtube',
          src: `https://www.youtube-nocookie.com/embed/${ytId}?rel=0`,
          watchUrl: `https://www.youtube.com/watch?v=${ytId}`,
          videoId: ytId,
          thumbnailUrl: `https://img.youtube.com/vi/${ytId}/maxresdefault.jpg`,
        };
      }
      // Still display Aparat embed or fallback
      return {
        kind: 'aparat',
        src: `https://www.aparat.com/video/video/embed/videohash/${hash}/vt/frame`,
        rawUrl: url,
      };
    }
  }

  // 3. Vimeo
  if (url.includes('vimeo.com')) {
    const vimeoId = extractVimeoId(url);
    if (vimeoId) {
      return {
        kind: 'vimeo',
        src: `https://player.vimeo.com/video/${vimeoId}?dnt=1&app_id=122963`,
      };
    }
  }

  // 4. Direct video file (.mp4, .webm)
  if (isDirectVideoUrl(url)) {
    return { kind: 'direct', src: url };
  }

  // 5. Existing valid embed iframe URL
  if (/^https?:\/\//i.test(url) && (url.includes('/embed') || url.includes('/video/'))) {
    return { kind: 'iframe', src: url };
  }

  return { kind: 'unavailable', rawUrl: url, reason: 'Invalid or unsupported video format' };
}
